// Marketplace capability registry — the single declarative "control panel" for
// what each marketplace may access, on two independent axes: its category
// (SELLER / VENDOR / BOOK) and its country.
//
// To change a rule: edit FEATURE_ALLOWED_CATEGORIES or FEATURE_ALLOWED_COUNTRIES.
// To gate a new area: add a MARKETPLACE_FEATURE key (+ a path prefix if it has its
// own routes) and consume it via <FeatureGate feature="…"> or
// accountStore.canAccessFeature(key).
//
// Why category and not a backend flag: on SP/seller datasets the backend
// resolves seller_id from the ads-profile (X-Profile-Id header), so VENDOR/KDP
// profiles always return empty by design — the profile's category is the
// correct, frontend-known gating axis. This is a flexible, co-designed system:
// profileCanAccessFeature() is the single seam — if the backend later exposes a
// better signal (has_sp_data / supported_datasets), swap the source here and no
// consumer changes.

import { marketplaceCategory, MARKETPLACE_CATEGORY } from '@/helpers/marketplaceAccountType.js';

export const MARKETPLACE_FEATURE = Object.freeze({
  BRAND_ANALYTICS: 'brand-analytics',
  BUSINESS_REPORTS: 'business-reports',
  INVENTORY: 'inventory',
  FINANCE: 'finance',
  LISTINGS: 'listings',
  ATTRIBUTION: 'attribution',
});

// Feature → categories allowed to access it. A feature absent from this map is
// available to every category. Phase-2 decision: the five seller sections are
// SELLER-only (VENDOR/KDP see no seller data on these datasets — confirmed).
export const FEATURE_ALLOWED_CATEGORIES = Object.freeze({
  [MARKETPLACE_FEATURE.BRAND_ANALYTICS]: [MARKETPLACE_CATEGORY.SELLER],
  [MARKETPLACE_FEATURE.BUSINESS_REPORTS]: [MARKETPLACE_CATEGORY.SELLER],
  [MARKETPLACE_FEATURE.INVENTORY]: [MARKETPLACE_CATEGORY.SELLER],
  [MARKETPLACE_FEATURE.FINANCE]: [MARKETPLACE_CATEGORY.SELLER],
  [MARKETPLACE_FEATURE.LISTINGS]: [MARKETPLACE_CATEGORY.SELLER],
});

// Feature → the marketplace country codes it exists in. A feature absent from this
// map is available in every marketplace. This is the SECOND gating axis, orthogonal
// to category: Amazon Attribution is an Amazon-side program that ships in ten
// markets only, so a JP/AU/SE marketplace has nothing to show there even for a
// Seller account (the ETL doesn't even enumerate those profiles).
//
// Both 'UK' and 'GB' are listed deliberately. An Amazon Ads GB profile reports
// countryCode 'UK' while the Selling Partner catalog says 'GB' — the same
// divergence helpers/spLinkStatus.js joins around and MarketplaceTreeSelect's
// search matches on both. Listing only one would hide the feature on whichever
// surface uses the other spelling.
//
// Keep in sync with ATTRIBUTION_MARKETS in wiseppc-marketing-stream-etl
// (src/config/attribution.py) — that list decides which profiles get data at all.
export const FEATURE_ALLOWED_COUNTRIES = Object.freeze({
  [MARKETPLACE_FEATURE.ATTRIBUTION]: ['US', 'CA', 'UK', 'GB', 'DE', 'FR', 'IT', 'ES', 'MX', 'NL'],
});

// Route path prefix → feature, for the router guard and page banner (resolves
// the feature without tagging every page's meta). Note: the "Listings" nav
// section's pages live under /business-reports, so there is no /listings prefix
// — those routes resolve to BUSINESS_REPORTS (also SELLER-only), which is
// correct.
export const FEATURE_BY_PATH_PREFIX = Object.freeze({
  '/brand-analytics': MARKETPLACE_FEATURE.BRAND_ANALYTICS,
  '/business-reports': MARKETPLACE_FEATURE.BUSINESS_REPORTS,
  '/inventory': MARKETPLACE_FEATURE.INVENTORY,
  '/finance': MARKETPLACE_FEATURE.FINANCE,
  // Search Term Results lives under /ppc but is a SELLING_PARTNER dataset, so it
  // needs the same seller-only gate as the sections above. Without this entry it
  // was the one SP page with no capability gate: a Vendor/KDP marketplace fell
  // through to the "not linked to a Selling Partner" banner alone, whose CTA
  // cannot help — the page is seller-only regardless of any SP connection.
  '/ppc/search-term-asins': MARKETPLACE_FEATURE.BUSINESS_REPORTS,
  '/attribution': MARKETPLACE_FEATURE.ATTRIBUTION,
});

// Axis 1 — account category. Permissive on unknown type (category === null while
// account data loads) so nav/routes don't flicker or over-block before the
// marketplace resolves — the gate bites only on a known non-allowed category
// (VENDOR / BOOK).
export const profileCategoryAllowsFeature = (profile, featureKey) => {
  const allowed = FEATURE_ALLOWED_CATEGORIES[featureKey];
  if (!allowed) return true;
  const category = marketplaceCategory(profile);
  return category === null || allowed.includes(category);
};

// Axis 2 — marketplace country. Permissive in exactly the same way: a marketplace
// object with no countryCode (still loading, or a shape that never carried one)
// must NOT be blocked, or the feature vanishes for everyone whose payload lacks
// the field. Compared case-insensitively — the ads and Selling Partner sources
// already disagree on the code itself ('UK' vs 'GB'), so don't also trust casing.
export const profileMarketAllowsFeature = (profile, featureKey) => {
  const countries = FEATURE_ALLOWED_COUNTRIES[featureKey];
  if (!countries) return true;
  const code = (profile?.countryCode || '').toString().toUpperCase();
  return !code || countries.includes(code);
};

// Can a given profile/marketplace access a feature? Both axes must pass; each is
// independently permissive on data it doesn't have yet (see above). Split into the
// two predicates so callers that need to know WHICH axis failed — the gate reason
// helper, the OlapPage banner copy — can ask, instead of re-deriving it.
export const profileCanAccessFeature = (profile, featureKey) =>
  profileCategoryAllowsFeature(profile, featureKey) &&
  profileMarketAllowsFeature(profile, featureKey);

// Resolve the gated feature for a route path, or null if the path is ungated.
export const featureForPath = (path) => {
  if (!path) return null;
  const prefix = Object.keys(FEATURE_BY_PATH_PREFIX).find(
    (p) => path === p || path.startsWith(`${p}/`),
  );
  return prefix ? FEATURE_BY_PATH_PREFIX[prefix] : null;
};
